import React from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import { isEmpty, has } from 'lodash-es'
import { TtIcon, TtIconButton } from '@23/react'
import { intl } from 'lang/intl'
import {
  DefaultAvatarComponent,
  IconComponent,
  TooltipComponent,
} from '../../../../components'
import { hex2rgba } from '../../../../utils'
import { useWebinarColors } from '../../../../hooks/useWebinarColors'
import { ChyronType } from '../../../../../firebase/model/chyron-model'
import { AttendeeDemoter } from './attendee-demoter'
import {
  useOverlayState,
  setChyron,
} from '../../../../../firebase/hooks/useOverlays'

import './SpeakerBaseDetailsComponent.scss'

const deriveSecondaryTextColor = (styles) => {
  if (!has(styles, 'color')) {
    return {}
  }

  return {
    ...styles,
    color: hex2rgba(styles.color, 0.85),
  }
}

const renderAvatar = (presenter, size, isGuestSpeaker) => {
  if (presenter.hidden_p && !isGuestSpeaker) {
    return (
      <div className="hidden-avatar-wrapper">
        <IconComponent color="858C94" name="visibility-off" size={[16, 16]} />
      </div>
    )
  }

  if (!isEmpty(presenter.avatar_url) && !isGuestSpeaker) {
    return <img src={presenter.avatar_url} alt="avatar" />
  }

  const avatarSize = size === 'small' ? 32 : 48

  return (
    <DefaultAvatarComponent
      identifier={presenter.name}
      width={avatarSize}
      height={avatarSize}
    />
  )
}

const renderInfo = (
  title,
  company,
  link,
  facebook,
  twitter,
  linkedin,
  isHidden,
  style,
  isGuestSpeaker,
  question
) => {
  if (isHidden && !isGuestSpeaker) {
    return <div className="info-hidden">Not displayed on registration page</div>
  }

  const formattedQuestion = question
    ? `${intl.formatMessage({ defaultMessage: 'Question' })}: ${question}`
    : ''

  const colors = deriveSecondaryTextColor(style)
  return (
    <div className="info" style={colors}>
      {title}
      {title && company ? <span> &#8226; </span> : null}
      {isGuestSpeaker ? formattedQuestion : company}

      <span className="social-icons">
        {link && (
          <a href={link} target="_new" aria-label="website">
            <TtIcon
              name="globe"
              library="one"
              style={colors}
              className="icon"
            />
          </a>
        )}
        {linkedin && (
          <a href={linkedin} target="_new" aria-label="linkedin">
            <TtIcon
              name="linkedin"
              library="one/logos"
              style={colors}
              className="icon"
            />
          </a>
        )}
        {facebook && (
          <a href={facebook} target="_new" aria-label="facebook">
            <TtIcon
              name="facebook"
              library="one/logos"
              style={colors}
              className="icon"
            />
          </a>
        )}
        {twitter && (
          <a href={twitter} target="_new" aria-label="twitter">
            <TtIcon
              name="twitter"
              library="one/logos"
              style={colors}
              className="icon"
            />
          </a>
        )}
      </span>
    </div>
  )
}

const hasSpeakerInfoChyronShown = (chyron, speaker) =>
  chyron?.type === ChyronType.SpeakerInfo &&
  chyron?.liveSpeakerId === speaker.live_speaker_id

const SpeakerBaseDetailsComponent = (props) => {
  const {
    speaker,
    children,
    isEditable,
    isAttendeeDemotable,
    viewOnly,
    style,
    size = 'small',
    showDemoteButton,
    showChyronControls,
  } = props
  const {
    name,
    title,
    company,
    link,
    facebook,
    twitter,
    linkedin,
    hidden_p: isHidden,
    question,
  } = speaker
  const className = classnames('speaker-base-details-component', `size-${size}`)
  const isGuestSpeaker =
    showDemoteButton && HasJoinToSpeakEnabled && isAttendeeDemotable
  const overlay = useOverlayState()
  const theme = useWebinarColors()

  const speakerLabel = hasSpeakerInfoChyronShown(overlay, speaker)
    ? intl.formatMessage(
        {
          defaultMessage: 'Hide speaker info card for {name}.',
        },
        { name }
      )
    : intl.formatMessage(
        {
          defaultMessage: 'Show speaker info card for {name}.',
        },
        { name }
      )

  return (
    <div className={className}>
      <div className="content">
        {React.isValidElement(children) && children}
        <div className="avatar">
          {renderAvatar(speaker, size, isGuestSpeaker)}
        </div>
        <div className="details" style={style}>
          <h4 className="name" style={style}>
            {name}
          </h4>
          {renderInfo(
            title,
            company,
            link,
            facebook,
            twitter,
            linkedin,
            isHidden,
            style,
            isGuestSpeaker,
            question
          )}
        </div>
        {isEditable && (
          <TtIconButton
            name="edit"
            library="one"
            class={classnames('edit-speaker-button', {
              disabled: viewOnly,
            })}
            aria-haspopup="dialog"
            aria-label={`Edit Speaker ${name}`}
            disabled={viewOnly ? 'disabled' : undefined}
          />
        )}
        {showChyronControls && (
          <TooltipComponent
            placement="topRight"
            title={
              hasSpeakerInfoChyronShown(chyron, speaker)
                ? intl.formatMessage({
                    defaultMessage: 'Hide Speaker Intro Card',
                  })
                : intl.formatMessage({
                    defaultMessage: 'Show Speaker Intro Card',
                  })
            }
          >
            <TtIconButton
              label={speakerLabel}
              name="eye"
              onClick={() => {
                if (hasSpeakerInfoChyronShown(chyron, speaker)) {
                  setChyron(null)
                } else {
                  setChyron({
                    type: ChyronType.SpeakerInfo,
                    name: speaker.name,
                    title: speaker.title,
                    avatarUrl: speaker.avatar_url,
                    bio: speaker.bio,
                    company: speaker.company,
                    liveSpeakerId: speaker.live_speaker_id,
                  })
                }
              }}
              style={{
                color: hasSpeakerInfoChyronShown(chyron, speaker)
                  ? theme.accent.color
                  : theme.sidebar.contrast.color,
                '--hover-color': theme.accent.color,
              }}
            />
          </TooltipComponent>
        )}
        {isGuestSpeaker && (
          <AttendeeDemoter
            attendeeId={speaker.live_speaker_id}
            attendeeName={speaker.name}
          />
        )}
      </div>
    </div>
  )
}

SpeakerBaseDetailsComponent.propTypes = {
  speaker: PropTypes.shape({
    title: PropTypes.string,
    name: PropTypes.string,
    company: PropTypes.string,
    link: PropTypes.string,
    linkedin: PropTypes.string,
    facebook: PropTypes.string,
    twitter: PropTypes.string,
    hidden_p: PropTypes.number,
    live_speaker_id: PropTypes.string,
    live_id: PropTypes.string,
    avatar_url: PropTypes.string,
    bio: PropTypes.string,
    question: PropTypes.string,
  }),
  children: PropTypes.node,
  isEditable: PropTypes.bool,
  isAttendeeDemotable: PropTypes.bool,
  viewOnly: PropTypes.bool,
  style: PropTypes.object,
  size: PropTypes.oneOf(['small', 'large']),
  showDemoteButton: PropTypes.bool,
  showChyronControls: PropTypes.bool,
}

SpeakerBaseDetailsComponent.defaultProps = {
  isEditable: false,
  showDemoteButton: false,
}

export default SpeakerBaseDetailsComponent
